import { CODELESS_ENTRY_CONST, REFI_WEB_UI_CONST } from '../../constants/constants.js';
import { DebugService } from '../debug/debug.service.js';

/**
 * Service for handling Kochava deep linking and browser detection
 * Manages control allocation routing through Kochava smart links
 */
class _KochavaService {
  constructor() {
    this.debug = new DebugService('KochavaService');
  }

  /**
   * Determines the browser type based on the user agent string.
   * Excludes certain browsers like Firefox, Edge, Opera, Brave, Vivaldi, and Samsung Browser.
   * Returns 'chrome' for Chrome-based browsers and 'safari' for Safari.
   * Returns undefined if the browser is not recognized.
   */
  getBrowserInfo() {
    const { userAgent } = window.navigator;

    const excludeBrowsers = [/Firefox|FxiOS/i, /Edg|EdgA/i, /OPR|Opera/i, /Brave/i, /Vivaldi/i, /SamsungBrowser/i];

    const isExcluded = excludeBrowsers.some(pattern => pattern.test(userAgent));

    if (isExcluded) return undefined;

    if (/Chrome/i.test(userAgent) || /CriOS/i.test(userAgent)) return 'chrome';

    if (/Safari/i.test(userAgent)) return 'safari';

    return undefined;
  }

  /**
   * Appends the originbrowser parameter to query string if browser is supported
   * @param {string} queryString - The existing query string
   * @returns {string} - Query string with originbrowser param if applicable
   */
  setBrowserType(queryString) {
    const browserType = this.getBrowserInfo();
    if (browserType) {
      queryString += queryString ? '%26' : '';
      queryString += `originbrowser=${browserType}`;
    }
    return queryString;
  }

  /**
   * Gets the appropriate Kochava URL based on environment
   * @returns {string} - Kochava URL for current environment
   */
  getKochavaUrl() {
    return REFI_WEB_UI_CONST.isProd() ? CODELESS_ENTRY_CONST.KOCHAVA_URLS.PRODUCTION : CODELESS_ENTRY_CONST.KOCHAVA_URLS.NON_PRODUCTION;
  }

  /**
   * Formats query parameters for Kochava URL
   * Replaces & with %26 and adds originbrowser param if applicable
   * @param {URLSearchParams} params - URL search params object
   * @returns {string} - Formatted query string
   */
  formatParams(params) {
    let formattedParams = params?.toString().replace(/&/g, '%26') || '';

    // Add originbrowser query param if params is not empty
    if (formattedParams) {
      formattedParams = this.setBrowserType(formattedParams);
    }

    return formattedParams;
  }

  /**
   * Redirects to Kochava smart link with formatted parameters
   * @param {string} formattedParams - Formatted query string with parameters
   */
  goToKochavaLogin(formattedParams) {
    const kochavaUrl = this.getKochavaUrl();
    const kochavaUrlWithParams = `${kochavaUrl}${formattedParams}`;

    this.debug.log('Redirecting to Kochava URL:', kochavaUrlWithParams);
    window.open(kochavaUrlWithParams.toString(), '_self');
  }

  /**
   * Handles Kochava login flow with SSO fallback
   * Opens Kochava link and redirects to SSO after delay
   * @param {URLSearchParams} params - URL search params (can be empty, null, or undefined)
   * @param {Function} ssoLoginCallback - Callback function to trigger SSO login
   */
  handleKochavaFlow(params, ssoLoginCallback) {
    if (typeof ssoLoginCallback !== 'function') {
      this.debug.log('handleKochavaFlow: invalid ssoLoginCallback');
      return false;
    }

    const formattedParams = this.formatParams(params);
    this.debug.log('Attempting Kochava login with formatted params:', formattedParams);

    // Open the Kochava URL
    this.goToKochavaLogin(formattedParams);

    // Set a timeout to navigate to SSO after a short delay
    // This allows the browser to process the app link before redirecting
    setTimeout(() => {
      this.debug.log('Kochava login flow initiated, redirecting to SSO login.');
      ssoLoginCallback();
    }, 1000); // 1-second delay

    return true;
  }
}

export const KochavaService = new _KochavaService();
